import { useEffect, useState } from "react";

import { useLoadCoverageByType } from "../api";

import type { CoverageTypeEnum } from "@platform/types";
import type { RfpCoverage, RfpData } from "@platform/plan-grid-core";

interface UsePlanDesignerCoverageSelectionArgs {
  rfp: RfpData;
  updateSelectedCoverage: (updatedCoverage?: RfpCoverage) => void;
  onCoverageSelect?: (coverageType: CoverageTypeEnum) => void;
}

export const usePlanDesignerCoverageSelection = ({
  rfp,
  updateSelectedCoverage,
  onCoverageSelect,
}: UsePlanDesignerCoverageSelectionArgs): {
  isLoadingCoverage: boolean;
  isLoading: boolean;
  handleCoverageSelect: (coverageType: CoverageTypeEnum) => Promise<void>;
} => {
  const [isLoadingCoverage, setIsLoadingCoverage] = useState(false);
  const [currentCoverageType, setCurrentCoverageType] =
    useState<CoverageTypeEnum | null>(null);

  const { data, isLoading } = useLoadCoverageByType(
    {
      coverageType: currentCoverageType || "",
      rfpId: rfp.id,
    },
    {
      enabled: !!currentCoverageType,
    },
  );

  const handleCoverageSelect = async (
    coverageType: CoverageTypeEnum,
  ): Promise<void> => {
    setIsLoadingCoverage(true);

    const selectedCoverage = rfp.coverages.find(
      (coverage) => coverage.type === coverageType,
    );

    if (selectedCoverage) {
      updateSelectedCoverage(selectedCoverage);
      setIsLoadingCoverage(false);
    } else {
      setCurrentCoverageType(coverageType);
    }

    onCoverageSelect?.(coverageType);
  };

  useEffect(() => {
    if (!currentCoverageType) {
      return;
    }

    setIsLoadingCoverage(isLoading);

    if (data) {
      updateSelectedCoverage(data);
    }
  }, [data, currentCoverageType, isLoading, updateSelectedCoverage]);

  return {
    isLoadingCoverage,
    isLoading,
    handleCoverageSelect,
  };
};
